import { NodeViewContent, type NodeViewProps, NodeViewWrapper } from "@tiptap/react";
import type { FrontMatterFieldAttrs, FrontMatterFieldError } from "@workith/editor-schema";
import {
  parseFrontMatterBoolean,
  parseFrontMatterTags,
  serializeFrontMatterTags,
} from "@workith/editor-schema";
import { Badge, Button, Input, Switch, Textarea } from "@workith/ui";
import { X } from "lucide-react";
import type { KeyboardEvent as ReactKeyboardEvent } from "react";
import { useState } from "react";
import {
  deleteFrontMatterField,
  replaceFrontMatterFieldText,
  updateFrontMatterFieldAttrs,
} from "./frontMatterCommands";
import {
  frontMatterFieldErrors,
  handleFrontMatterControlKeyDown,
  useFrontMatterEditorSync,
} from "./frontMatterControls";

const CONTROL_CLASS = "h-8 text-sm";

interface FieldLocation {
  fieldPos: number | null;
  cardPos: number | undefined;
  errors: FrontMatterFieldError[];
}

/**
 * 훅이 아니라 순수 계산이다. 오류는 카드 전체를 함께 봐야 계산할 수 있으므로(중복 키) 이 필드의
 * 위치에서 부모 카드를 찾고, 카드 단위로 캐시된 검증 결과에서 이 행의 오류만 골라낸다.
 */
function resolveFieldLocation(props: NodeViewProps): FieldLocation {
  const rawPos = props.getPos();
  if (typeof rawPos !== "number") return { fieldPos: null, cardPos: undefined, errors: [] };

  const resolved = props.editor.state.doc.resolve(rawPos);
  if (resolved.depth === 0) return { fieldPos: rawPos, cardPos: undefined, errors: [] };

  return {
    fieldPos: rawPos,
    cardPos: resolved.before(resolved.depth),
    errors: frontMatterFieldErrors(resolved.parent, resolved.index()),
  };
}

export function FrontMatterFieldNodeView(props: NodeViewProps) {
  const { editor, node } = props;
  const editable = useFrontMatterEditorSync(editor);
  const { fieldPos, cardPos, errors } = resolveFieldLocation(props);

  const attrs = node.attrs as FrontMatterFieldAttrs;
  const text = node.textContent;
  const label = attrs.key || "새 필드";
  const disabled = !editable || fieldPos === null;

  const controlProps = {
    "data-frontmatter-control": true,
    disabled,
    onKeyDown: (event: ReactKeyboardEvent<HTMLElement>) =>
      handleFrontMatterControlKeyDown(event, editor, cardPos),
  } as const;

  const writeText = (value: string) => {
    if (fieldPos !== null) replaceFrontMatterFieldText(editor, fieldPos, value);
  };

  return (
    <NodeViewWrapper
      as="div"
      data-type="frontMatterField"
      data-key={attrs.key}
      data-value-kind={attrs.valueKind}
      className="px-4 py-2"
    >
      <div className="flex items-start gap-3" contentEditable={false}>
        <Input
          {...controlProps}
          aria-label={`${label} 키`}
          className={`${CONTROL_CLASS} w-40 shrink-0 font-medium`}
          value={attrs.key}
          onChange={
            fieldPos === null
              ? undefined
              : (event) =>
                  updateFrontMatterFieldAttrs(editor, fieldPos, { key: event.target.value })
          }
        />
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <FieldValueControl
            attrs={attrs}
            label={label}
            text={text}
            controlProps={controlProps}
            writeText={writeText}
          />
        </div>
        <Button
          {...controlProps}
          type="button"
          size="sm"
          variant="ghost"
          aria-label={`${label} 필드 삭제`}
          className="h-8 shrink-0 px-2"
          onMouseDown={(event) => event.preventDefault()}
          onClick={fieldPos === null ? undefined : () => deleteFrontMatterField(editor, fieldPos)}
        >
          <X className="h-4 w-4" aria-hidden="true" />
        </Button>
      </div>
      {/* ProseMirror content DOM은 유지하되 사용자가 값을 두 곳에서 편집하지 않게 감춘다. */}
      <NodeViewContent className="sr-only" aria-hidden="true" />
      {errors.length > 0 && (
        <div contentEditable={false}>
          {errors.map((error) => (
            <p key={error.code} role="alert" className="mt-1 text-xs text-destructive">
              {error.message}
            </p>
          ))}
        </div>
      )}
    </NodeViewWrapper>
  );
}

interface ValueControlProps {
  attrs: FrontMatterFieldAttrs;
  label: string;
  text: string;
  controlProps: {
    "data-frontmatter-control": true;
    disabled: boolean;
    onKeyDown: (event: ReactKeyboardEvent<HTMLElement>) => void;
  };
  writeText: (value: string) => void;
}

function FieldValueControl(props: ValueControlProps): JSX.Element {
  const { attrs, label, text, controlProps, writeText } = props;

  if (attrs.valueKind === "complex") return <ComplexValueControl {...props} />;
  if (attrs.valueKind === "array") return <TagListControl {...props} />;

  if (attrs.scalarType === "boolean") {
    return (
      <Switch
        {...controlProps}
        aria-label={`${label} 값`}
        checked={parseFrontMatterBoolean(text) === true}
        onCheckedChange={(checked) => writeText(checked ? "true" : "false")}
      />
    );
  }

  return (
    <div className="flex items-center gap-2">
      <Input
        {...controlProps}
        type={attrs.scalarType === "date" ? "date" : "text"}
        aria-label={`${label} 값`}
        className={CONTROL_CLASS}
        value={text}
        onChange={(event) => writeText(event.target.value)}
      />
      {attrs.scalarType === "null" && (
        <span className="shrink-0 text-xs text-muted-foreground">null</span>
      )}
    </div>
  );
}

/** 여러 줄 YAML은 기본적으로 접어 두되, 접어도 노드 텍스트는 그대로 유지한다. */
function ComplexValueControl({ label, text, controlProps, writeText }: ValueControlProps) {
  const [open, setOpen] = useState(false);

  return (
    <>
      <Button
        {...controlProps}
        type="button"
        size="sm"
        variant="outline"
        aria-label={`${label} YAML 편집`}
        aria-expanded={open}
        className="h-8 w-fit"
        onMouseDown={(event) => event.preventDefault()}
        onClick={() => setOpen(!open)}
      >
        YAML 편집
      </Button>
      {open && (
        <Textarea
          {...controlProps}
          aria-label={`${label} 값`}
          className="min-h-[80px] font-mono text-xs"
          value={text}
          onChange={(event) => writeText(event.target.value)}
        />
      )}
    </>
  );
}

interface TagEntry {
  tag: string;
  index: number;
  key: string;
}

/** 같은 태그가 반복돼도 React key가 겹치지 않도록 등장 횟수를 붙인다. */
function tagEntries(tags: string[]): TagEntry[] {
  const occurrences = new Map<string, number>();
  const entries: TagEntry[] = [];
  for (const tag of tags) {
    const occurrence = (occurrences.get(tag) ?? 0) + 1;
    occurrences.set(tag, occurrence);
    entries.push({ tag, index: entries.length, key: `${tag}#${occurrence}` });
  }
  return entries;
}

/**
 * 태그 목록은 노드 텍스트의 flow sequence에서만 파생한다. 파싱할 수 없는 원문은
 * 태그로 바꾸지 않고 그대로 편집하게 두어 사용자의 입력을 잃지 않는다.
 */
function TagListControl({ attrs, label, text, controlProps, writeText }: ValueControlProps) {
  const [draft, setDraft] = useState("");
  const tags = parseFrontMatterTags(text);

  if (tags === null) {
    return (
      <Input
        {...controlProps}
        aria-label={`${label} 값`}
        className={CONTROL_CLASS}
        value={text}
        onChange={(event) => writeText(event.target.value)}
      />
    );
  }

  const commitDraft = () => {
    const value = draft.trim();
    if (value.length === 0) return;
    writeText(serializeFrontMatterTags([...tags, value]));
    setDraft("");
  };

  return (
    <div className="flex flex-wrap items-center gap-2">
      {tagEntries(tags).map((entry) => (
        <Badge key={entry.key} variant="outline" data-frontmatter-tag={entry.tag} className="gap-1">
          {entry.tag}
          <Button
            {...controlProps}
            type="button"
            size="sm"
            variant="ghost"
            aria-label={`${attrs.key} 태그 ${entry.tag} 삭제`}
            className="h-4 w-4 p-0"
            onMouseDown={(event) => event.preventDefault()}
            onClick={() =>
              writeText(serializeFrontMatterTags(tags.filter((_, i) => i !== entry.index)))
            }
          >
            <X className="h-3 w-3" aria-hidden="true" />
          </Button>
        </Badge>
      ))}
      <Input
        {...controlProps}
        aria-label={`${attrs.key} 태그 추가`}
        className={`${CONTROL_CLASS} w-32`}
        placeholder="태그 추가"
        value={draft}
        onChange={(event) => setDraft(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === "Enter") {
            event.preventDefault();
            commitDraft();
            return;
          }
          controlProps.onKeyDown(event);
        }}
      />
    </div>
  );
}
